import { EPP_URL, EPP_UTM } from '../../config/constants'
import type { Environment } from '../../config/constants'
import type { AvailabilityInfo, Employee } from './EmployeesList.types'

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function toLocaleDateString(isoString: string): string {
  // Support both "YYYY-MM-DD" and full ISO datetime strings
  const datePart = isoString.split('T')[0] ?? isoString
  const [, monthStr, dayStr] = datePart.split('-')
  const month = parseInt(monthStr ?? '1', 10) - 1
  const day = parseInt(dayStr ?? '1', 10)
  return `${MONTH_NAMES[month]} ${day}`
}

function todayISODate(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function tomorrowISODate(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Returns structured availability info for an employee.
 * The caller passes `info.key` (and optionally `info.date`) to `t()`.
 *
 * "Available today"     → green text (status: "today")
 * "Available tomorrow"  → regular text (status: "tomorrow")
 * "Available on Apr 5"  → regular text (status: "future")
 */
export function getAvailabilityInfo(
  firstAvailableDate: string | undefined | null,
): AvailabilityInfo {
  if (!firstAvailableDate) {
    return { key: 'employeesList.unavailable', status: 'unavailable' }
  }

  const datePart = firstAvailableDate.split('T')[0] ?? firstAvailableDate

  if (datePart === todayISODate()) {
    return { key: 'employeesList.availableToday', status: 'today' }
  }
  if (datePart === tomorrowISODate()) {
    return { key: 'employeesList.availableTomorrow', status: 'tomorrow' }
  }

  return {
    key: 'employeesList.availableOn',
    date: toLocaleDateString(firstAvailableDate),
    status: 'future',
  }
}

/**
 * Sorts employees by firstAvailableDate ascending.
 * Employees with no availability (null) are pushed to the end.
 */
export function sortByEarliestAvailability(employees: Employee[]): Employee[] {
  return [...employees].sort((a, b) => {
    if (!a.firstAvailableDate && !b.firstAvailableDate) return 0
    if (!a.firstAvailableDate) return 1
    if (!b.firstAvailableDate) return -1
    return a.firstAvailableDate.localeCompare(b.firstAvailableDate)
  })
}

/**
 * Returns the Employee Profile Page URL for a given employee.
 * Matches the old booking-tool's _eppUrl logic exactly.
 * Returns null when the employee has no real imageUrl (no-preference entries
 * and employees without a photo do not get a profile link).
 */
export function getEppUrl({
  employee,
  env,
  storeId,
  locationCode,
}: {
  employee: Employee
  env: Environment
  storeId?: string
  locationCode?: string
}): string | null {
  if (!employee.imageUrl || !employee.employeeId || !employee.name) return null

  let url = `${EPP_URL[env]}${employee.employeeId}/${employee.name}${EPP_UTM}`

  if (storeId) url += `&storeId=${storeId}`
  if (locationCode) url += `&locationCode=${locationCode}`

  return url
}

/** Returns the best available photo URL for an employee, or null when neither is set. */
export function getEmployeeImage(
  imageUrl: string | null,
  imageAlternativeUrl: string | null,
): string | null {
  return imageUrl ?? imageAlternativeUrl
}
